import { ReactNode, useMemo } from "react";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { DarkVariant, DARK_VARIANTS, ResolvedTheme } from "./theme";
import { useTheme } from "./useTheme";

/**
 * Material-UI's own theme, kept in step with the app theme.
 *
 * The dark theme is otherwise pure SCSS, which is enough for our own markup but
 * NOT for Material-UI: every colour inside a MUI component comes from its JS
 * theme, is emitted as an emotion class with a hashed name (`css-h7be85`) and
 * is therefore out of reach of a stylesheet. Without a provider MUI falls back
 * to its default LIGHT palette, so dark mode repainted only the outer
 * `.MuiPaper-root` (which we can select) and left the insides light — most
 * visibly the date/time picker, whose clock numbers render at
 * `rgba(0,0,0,0.87)` on a near-black dial, i.e. invisible.
 *
 * Colours are read from the live `--dark-*` custom properties rather than
 * hardcoded, so the MUI theme follows the chosen dark VARIANT (charcoal,
 * midnight, ...) exactly like the rest of the theme does.
 */

/** `$primary` in _bootstrap-variables.scss — MUI otherwise uses its own blue. */
const PRIMARY_LIGHT = "#1868fb";

const token = (name: string, fallback: string): string => {
  if (typeof window === "undefined" || !window.getComputedStyle) {
    return fallback;
  }
  const value = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  // Empty in jsdom and before the stylesheet has loaded.
  return value || fallback;
};

export const buildTheme = (resolved: ResolvedTheme, variant: DarkVariant) => {
  if (resolved === "light") {
    return createTheme({ palette: { primary: { main: PRIMARY_LIGHT } } });
  }
  // The variant's own tones are the fallback, so a theme built before the
  // stylesheet is readable still lands on the right colours rather than on
  // charcoal's.
  const tones =
    DARK_VARIANTS.find((candidate) => candidate.value === variant) ||
    DARK_VARIANTS[0];
  // Floating MUI surfaces (the picker popover) use the lifted panel tone; see
  // the matching `.MuiPaper-root:not(.MuiDrawer-paper)` rule in _dark.scss.
  // `background.paper` must agree with it, because MUI paints elements *on*
  // that surface — a calendar day cell, for one — with this exact colour and
  // expects them to disappear into it.
  const panel = token("--dark-panel", tones.panel);
  return createTheme({
    palette: {
      mode: "dark",
      primary: { main: token("--dark-accent", "#4a68e8") },
      background: { default: token("--dark-bg", tones.swatch), paper: panel },
      text: {
        primary: token("--dark-text", "#e7e9ef"),
        secondary: token("--dark-text-muted", "#8b92a4"),
      },
      divider: token("--dark-border", "#262d3b"),
    },
  });
};

const MuiThemeProvider = ({ children }: { children: ReactNode }) => {
  const { resolvedTheme, darkVariant } = useTheme();
  const theme = useMemo(() => buildTheme(resolvedTheme, darkVariant), [
    resolvedTheme,
    darkVariant,
  ]);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default MuiThemeProvider;
